import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clamp } from "lodash";
import Input from "components/form/input/Input";
import "./websiteBuilderRange.scss";
import { isWholeNumber } from "helpers/Helpers";

interface IWebsiteBuilderRangeProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  setValue: (updatedValue: number) => void;
}

interface IWebsiteBuilderRangeState {
  dragging: boolean;
  draggingMouseOffset: number;
  dragSliderWidth: number;
  dragSliderStops: number[];
  valueInput: string;
}

const HANDLE_DIMENSIONS = 24;
const HALF_HANDLE_DIMENSIONS = HANDLE_DIMENSIONS / 2;

export function WebsiteBuilderRange(props: IWebsiteBuilderRangeProps) {
  const { label, min, max, step, value, setValue } = props;

  const sliderRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<IWebsiteBuilderRangeState>({
    dragging: false,
    draggingMouseOffset: null,
    dragSliderWidth: null,
    dragSliderStops: null,
    valueInput: value == null ? "" : String(value),
  });

  function isValueInputValid(valueInput: string) {
    const value = Number(valueInput);

    return isWholeNumber(valueInput) && value >= min && value <= max;
  }

  const valueInputValid = isValueInputValid(state.valueInput);

  useEffect(() => {
    if (!sliderRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      let updatedDragSliderWidth = sliderRef.current?.getBoundingClientRect()?.width;

      if (updatedDragSliderWidth == null) {
        setState(prev => ({ ...prev, dragSliderWidth: null, dragSliderStops: null }));
      } else {
        updatedDragSliderWidth -= HANDLE_DIMENSIONS;

        const updatedDragSliderStops: number[] = [];

        const stepPercent = step / (max - min);
        const adjustmentStep = stepPercent * updatedDragSliderWidth;

        let adjustment = 0;
        let adjustmentIndex = 0;

        while (updatedDragSliderWidth - adjustment > 0.01) {
          adjustment = adjustmentIndex * adjustmentStep;
          updatedDragSliderStops.push(adjustment);
          adjustmentIndex++;
        }

        setState(prev => ({
          ...prev,
          dragSliderWidth: updatedDragSliderWidth,
          dragSliderStops: updatedDragSliderStops,
        }));
      }
    });

    resizeObserver.observe(sliderRef.current);
    return () => resizeObserver.disconnect();
  }, [step, max, min]);

  const handleX = useMemo(() => {
    if (state.dragSliderWidth != null && value != null) {
      const valuePercent = (value - min) / (max - min);
      const updatedHandleX = valuePercent * state.dragSliderWidth;

      return updatedHandleX;
    } else {
      return 0;
    }
  }, [state.dragSliderWidth, value, min, max]);

  function calculateValue(
    clientX: number,
    offset: number,
    dragSliderStops: number[],
    min: number,
    max: number,
    step: number,
  ) {
    const sliderX = sliderRef.current?.getBoundingClientRect()?.x;

    if (sliderX == null || dragSliderStops == null || dragSliderStops.length === 0) {
      setValue(min);

      setState(prev => ({
        ...prev,
        valueInput: String(min),
      }));
    }

    const handleX = clientX - offset - sliderX;

    let lowestDelta: number = null;
    let lowestDeltaIndex: number = 0;

    for (let i = 0; i < dragSliderStops.length; i++) {
      const delta = Math.abs(dragSliderStops[i] - handleX);

      if (lowestDelta == null || delta < lowestDelta) {
        lowestDelta = delta;
        lowestDeltaIndex = i;
      }
    }

    const updatedValue = clamp(min + lowestDeltaIndex * step, min, max);

    setValue(updatedValue);

    setState(prev => ({
      ...prev,
      valueInput: String(updatedValue),
    }));
  }

  const handlePointerMove = useCallback(
    (e: MouseEvent) => {
      calculateValue(e.clientX, state.draggingMouseOffset, state.dragSliderStops, min, max, step);
    },
    [state.draggingMouseOffset, state.dragSliderStops, min, max, step],
  );

  const handlePointerUp = useCallback((e: PointerEvent) => {
    handleRef.current?.releasePointerCapture(e.pointerId);

    setState(prev => ({ ...prev, dragging: false, draggingMouseOffset: null }));
  }, []);

  useEffect(() => {
    if (state.dragging) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    }

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [state.dragging, handlePointerMove, handlePointerUp]);

  function calculateSliderGradient() {
    const blue = "#3D5FA0";
    const grey = "#EAECF0";
    const progressX = handleX + HALF_HANDLE_DIMENSIONS;

    return `linear-gradient(
        to right,
        ${blue} 0%,
        ${blue} ${progressX}px,
        ${grey} ${progressX}px,
        ${grey} 100%
      )`;
  }

  return (
    <div className="website-builder-range">
      <div>{label}</div>
      <div className="range-content">
        <div
          ref={sliderRef}
          className="range-slider"
          style={{
            background: calculateSliderGradient(),
          }}
          onPointerDown={e => {
            if (e.button === 0) {
              calculateValue(e.clientX, HALF_HANDLE_DIMENSIONS, state.dragSliderStops, min, max, step);
            }
          }}
        >
          <div
            ref={handleRef}
            className="range-slider-handle"
            style={{
              left: handleX,
              width: HANDLE_DIMENSIONS,
              height: HANDLE_DIMENSIONS,
              cursor: state.dragging ? "grabbing" : "grab",
            }}
            onPointerDown={e => {
              e.stopPropagation();

              const ref = handleRef.current;

              if (e.button === 0 && ref) {
                ref.setPointerCapture(e.pointerId);

                setState(prev => ({
                  ...prev,
                  dragging: true,
                  draggingMouseOffset: e.clientX - ref.getBoundingClientRect().x,
                }));
              }
            }}
          />
        </div>
        <Input
          className="range-value-input"
          labelHidden
          value={state.valueInput}
          error={!valueInputValid}
          onChange={e => {
            const updatedValueInput = e.target.value;

            setState(prev => ({
              ...prev,
              valueInput: updatedValueInput,
            }));

            if (isValueInputValid(updatedValueInput)) {
              setValue(Number(updatedValueInput));
            }
          }}
        />
      </div>
    </div>
  );
}
